<template>
  <div id="academic_undergrad_accounting">
    <usc-hero :title="this.hero.title" :loResSrc="this.hero.loResSrc" :hiResSrc="this.hero.hiResSrc" :color="this.page.color"></usc-hero>
    <usc-intro>
      <h2 slot="title">Overview</h2>
      <p>The USC Leventhal School of Accounting, an integral part of the Marshall School of Business, offers undergraduate and graduate degrees in accounting.</p>
    </usc-intro>
    <div class="body gutter-lg">
      <p><i style="">The accounting profession is dynamic, challenging and evolving. In today’s global economy, accountants require a wider knowledge base than ever before. Now that accounting firms have evolved into broad-based professional services firms, accountants need to understand systems operations, be technologically savvy, and possess good business sense and excellent communication skills.</i></p>
      <img src="../../../static/leventhal-No4_USNews.png" alt="USC Leventhal - Ranked #4 by the U.S. News & World Report" style="width: 100%;" />
      <p>The Leventhal School of Accounting emphasizes the importance of ethical behavior and principles throughout the curriculum, ensuring accounting majors develop these essential competencies for the profession.</p>
      <p>Pursuing an accounting degree at USC sets you on a path to be part of a close-knit community of students, faculty, staff and alumni. Leventhal’s strong ties with the global business community connect students to a broad array of job opportunities and internships that provide invaluable firsthand business experiences throughout the nation as well as in Europe, Asia and Latin America. In increasingly high demand, Leventhal graduates are equipped for successful careers ranging from public and forensic accounting to financial services and investment banking.</p>
    </div>
    <usc-table :title="this.table.title" :data="this.table.data" :disclaimer="this.table.disclaimer"></usc-table>
    <usc-calloutLink :url="this.calloutLink.url" :urltext="this.calloutLink.urlText"></usc-calloutLink>
    <div class="body gutter-lg mb">
      <h2>Progressive Degree Programs</h2>
      <p>Select USC students have the option of simplified early admission to our Master of Accounting (MAcc) or Master of Business Taxation (MBT) programs. The curriculum is identical to our full-time master’s programs and provides an opportunity for outstanding USC undergrads to apply to our graduate programs without going through the regular application process. The Progressive Degree also provides an opportunity for a select group of students to earn their MAcc or MBT degree in only nine to 12 months.</p>
    </div>
    <usc-course-chart :dataSet="courseData">
      <img slot="image" src="../../../static/USC_Marshall_CurriculumMap_2023_ACCT_R3.png" alt="Course chart for Accounting">
    </usc-course-chart>
  </div>
</template>

<script>
import hero from '@/components/Hero'
import intro from '@/components/Intro'
import table from '@/components/Table'
import Cta from '@/components/Cta'
import CourseChart from '@/components/CourseChart/CourseChart'
import calloutLink from '@/components/CalloutLink'

export default {
  name: 'UndergradAccounting',
  components: {
    'usc-hero': hero,
    'usc-intro': intro,
    'usc-table': table,
    'usc-cta': Cta,
    'usc-course-chart': CourseChart,
    'usc-calloutLink': calloutLink
  },
  data () {
    return {
      page: {
        color: '#ffcc00'
      },
      hero: {
        title: 'Undergraduate Accounting',
        loResSrc: '../../../static/HeroImages/Marshall99.jpg',
        hiResSrc: '../../../static/HeroImages/Marshall99.jpg'
      },
      calloutLink: {
        title: 'Your Future, Your Way',
        url: 'https://curriculum.marshall.usc.edu',
        urlText: 'Your Future, Your Way'
      },
      table: {
        title: 'Required Courses:',
        data: [
          {
            left: 'External Financial Reporting Issues',
            right: ''
          },
          {
            left: 'Introduction to Accounting Systems',
            right: ''
          },
          {
            left: 'Accounting Ethics',
            right: ''
          },
          {
            left: 'Tax Analysis with Data Analytics',
            right: ''
          },
          {
            left: 'Auditing with Data Analytics',
            right: ''
          }
        ],
        disclaimer: ''
      },
      courseData: {
        title: 'Leventhal School of Accounting Undergraduate Program',
        subtitle: 'Four-Year Sample Course Sequence',
        key: [
          {
            courseColor: 'marshall-core',
            courseLabel: 'Marshall Core'
          },
          {
            courseColor: 'usc-requirement',
            courseLabel: 'USC Requirement'
          },
          {
            courseColor: 'usc-elective',
            courseLabel: 'USC Elective/Minor'
          },
          {
            courseColor: 'marshall-elective',
            courseLabel: 'Accounting'
          }
        ],
        years: {
          freshman: {
            yearTitle: 'FRESHMAN',
            semesters: {
              Fall: [
                {
                  courseID: 'Math 118',
                  courseName: 'Fundamental Principles of the Calculus',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'BUAD 304',
                  courseName: 'Organizational Behavior and Leadership',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'WRIT 150',
                  courseName: 'Writing and Critical Reasoning',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                }
              ],
              Spring: [
                {
                  courseID: 'ECON 351x',
                  courseName: 'Microeconomics for Business',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'BUAD 280',
                  courseName: 'Introduction to Financial Accounting',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: 'GESM',
                  courseName: 'General Education Seminar',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Optional Elective',
                  courseName: 'BUAD 104',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 0,
                  courseCredits: 2
                }
              ]
            }
          },
          sophmore: {
            yearTitle: 'SOPHOMORE',
            semesters: {
              Fall: [
                {
                  courseID: 'BUAD 281',
                  courseName: 'Introduction to Managerial Accounting',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: 'BUAD 306 or 308',
                  courseName: 'Business Finance or Advanced Business Finance',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'BUAD 302',
                  courseName: 'Communication Strategy in Business',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: '4',
                  courseCredits: '4'
                },
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Free Elective',
                  courseName: '',
                  courseColor: 'usc-elective',
                  hasMin: true,
                  min: 1,
                  courseCredits: 3
                }
              ],
              Spring: [
                {
                  courseID: 'BUAD 310 or 312',
                  courseName: 'Applied Business Stats or Stats & Data Science for Business',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'ACCT 370',
                  courseName: 'External Financial Reporting Issue',
                  courseColor: 'marshall-elective',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'ACCT 371',
                  courseName: 'Introduction to Accounting Systems',
                  courseColor: 'marshall-elective',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                }
              ]
            }
          },
          junior: {
            yearTitle: 'JUNIOR',
            semesters: {
              Fall: [
                {
                  courseID: 'ECON 352x',
                  courseName: 'Macroeconomics for Business',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'ACCT 456',
                  courseName: 'Audit with Data Analytics',
                  courseColor: 'marshall-elective',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'ACCT 451',
                  courseName: 'Tax with Data Analytics',
                  courseColor: 'marshall-elective',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Free Elective',
                  courseName: '',
                  courseColor: 'usc-elective',
                  hasMin: false,
                  min: 2,
                  courseCredits: 2
                }
              ],
              Spring: [
                {
                  courseID: 'BUAD 307',
                  courseName: 'Marketing Fundamentals',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'BUAD 311 or 313',
                  courseName: 'Operations Mgmt or Advanced Operations Mgmt & Analytics',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'ACCT Upper Division Elective',
                  courseName: '',
                  courseColor: 'marshall-elective',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Free Elective',
                  courseName: '',
                  courseColor: 'usc-elective',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Ethnics Elective',
                  courseName: '',
                  courseColor: 'usc-elective',
                  hasMin: false,
                  min: 2,
                  courseCredits: 2
                }
              ]
            }
          },
          senior: {
            yearTitle: 'SENIOR',
            semesters: {
              Fall: [
                {
                  courseID: 'ACCT Upper Division Elective',
                  courseName: '',
                  courseColor: 'marshall-elective',
                  hasMin: false,
                  min: '4',
                  courseCredits: '4'
                },
                {
                  courseID: 'WRIT 340',
                  courseName: 'Advanced Writing',
                  courseColor: 'usc-requirement',
                  hasMin: true,
                  min: 3,
                  courseCredits: 4
                },
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                }
              ],
              Spring: [
                {
                  courseID: 'BUAD 497',
                  courseName: 'Strategic Management',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 2,
                  courseCredits: 2
                },
                {
                  courseID: 'ACCT 430',
                  courseName: 'Accounting Ethnics',
                  courseColor: 'marshall-elective',
                  hasMin: false,
                  min: 2,
                  courseCredits: 2
                },
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                }
              ]
            }
          }
        }
      }
    }
  },
  created () {
    this.$root.setPageHeader('Academics', 'Undergraduate Accounting Program')
  }
}
</script>

<style scoped lang="scss">

</style>



// WEBPACK FOOTER //
// src/pages/academics/UndergradAccounting.vue